import { ServiceBooking } from '@turizi/shared';
import { Badge, Checkbox } from '@mantine/core';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDayShort, formatPrice } from '@/lib/format-utils';
import { RESERVATIONS_PATH } from '@/lib/navigation';
import { RESERVATION_STATUS } from '@turizi/shared';
import { TagIcon } from '@/lib/tag-utils';
import { ReservationClient } from '../reservation-ui';
import { MoneyNumberInput, BookingGroupSize } from '../snippets';
import { ServiceTitleStatic } from '../services/service-title';

interface Props {
    booking: ServiceBooking;
    isSelected: boolean;
    onSelectChange: (selected: boolean) => void;
    appliedAmount: number;
    onAmountChange: (amount: number) => void;
}

export default function BookingCostRow({
    booking,
    isSelected,
    onSelectChange,
    appliedAmount,
    onAmountChange,
}: Props) {
    const { t } = useTranslation();

    const reservation = booking.reservation;
    const service = booking.service || booking.service_data;
    const costAmt = Number(booking.money_out_amount || 0);
    const curr = booking.money_out_currency || '$';

    const resStatus = reservation?.status || 'confirmed';
    const resStatusColor = RESERVATION_STATUS[resStatus]?.color || '#3b82f6';
    const resStart = reservation?.start_day;
    const resEnd = reservation?.end_day;

    return (
        <div
            className={`p-3 rounded-lg border text-xs flex flex-col gap-2 transition-all ${isSelected
                ? 'bg-slate-50 border-slate-400 shadow-sm'
                : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
        >
            {/* Header row: Checkbox, Booking ID, Reservation badge & dates, Client business, Cost */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                    <Checkbox
                        size="xs"
                        color='black'
                        checked={isSelected}
                        onChange={(e) => onSelectChange(e.currentTarget.checked)}
                    />

                    {/* Booking ID */}
                    <span className="font-semibold text-slate-400 text-[11px]">
                        #{booking.id}
                    </span>

                    {/* Reservation Badge & Dates */}
                    {booking.reservation_id && (
                        <div className="flex items-center gap-1.5 bg-slate-100 px-2 py-0.5 rounded-md">
                            <Badge
                                circle
                                color={resStatusColor}
                                className="!w-2 !h-2 !p-0 shrink-0"
                            />
                            <Link
                                to={`${RESERVATIONS_PATH}/${booking.reservation_id}`}
                                target="_blank"
                                className="font-bold text-sky-600 hover:underline text-[11px]"
                            >
                                #{booking.reservation_id}
                            </Link>
                            {resStart && (
                                <span className="text-[10px] text-slate-500 font-medium">
                                    {formatDayShort(resStart)} {resEnd && resEnd !== resStart ? `- ${formatDayShort(resEnd)}` : ''}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Client Business */}
                    {reservation && (
                        <div className="text-[11px] font-medium text-slate-700 flex items-center gap-1">
                            <ReservationClient reservation={reservation} icon={false} />
                        </div>
                    )}
                </div>

                {/* Booking Cost Amount */}
                <div className="font-bold text-red-600 text-xs flex items-baseline gap-1 shrink-0">
                    <span>{formatPrice(costAmt)}</span>
                    <span className="text-[10px] font-bold text-slate-800">{curr}</span>
                </div>
            </div>

            {/* Service Booking Info row: Booking date, Pax Info, Service title */}
            <div className="flex flex-wrap items-center justify-between gap-2 pl-6 bg-slate-50/50 p-2 rounded-md border border-slate-100">
                <div className="flex flex-col space-y-1">
                    <div className='flex gap-2 items-center'>
                        {/* Booking start day */}
                        <div className="font-semibold text-slate-700 text-sm whitespace-nowrap">
                            {formatDayShort(booking.start_day, '--/--')}
                        </div>
                        {/* Pax Group Pill */}
                        <BookingGroupSize booking={booking} />
                    </div>

                    {/* Service Title */}
                    {service && (
                        <div className="flex items-center gap-1 text-slate-800 font-medium text-xs">
                            <TagIcon name={service.category} size="xs" noBackground hideText />
                            <span>{ServiceTitleStatic(service)}</span>
                            {service.id && (
                                <span className="text-[10px] text-slate-400 font-normal">
                                    #{service.id}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Observations / self attrs if any */}
                {booking.observations && (
                    <div className="text-[10px] text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded font-medium max-w-[250px] truncate" title={booking.observations}>
                        {booking.observations}
                    </div>
                )}
            </div>

            {/* If selected: Amount input */}
            {isSelected && (
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 mt-1 pl-6">
                    <span className="text-[11px] text-slate-500 font-medium">{t('amount')}:</span>
                    <MoneyNumberInput
                        size="sm"
                        className="w-32"
                        amount={appliedAmount}
                        onChange={(val) => onAmountChange(Number(val || 0))}
                    />
                </div>
            )}
        </div>
    );
}
